import type { ThemeData } from 'src/pages/procurer/preferences/Themes/schema';
import type { ScoringData } from 'src/pages/procurer/preferences/Scoring/schema';
import type { GeographyData } from 'src/pages/procurer/preferences/Geography/schema';
import type { FinalizingData } from 'src/pages/procurer/preferences/Finalizing/schema';
import type { QualitativeData } from 'src/pages/procurer/preferences/Qualitative/schema';
import type {
  ActivityData,
  ReportedActivityData,
} from 'src/pages/procurer/preferences/Impact/schema';

import { create } from 'zustand';

export type ImpactData = {
  activities: ActivityData[];
  committedActivities?: ReportedActivityData[];
};

export type StoreMode = 'preferences' | 'contract';

export type PreferencesStep =
  | 'start'
  | 'geography'
  | 'themes'
  | 'impact'
  | 'scoring'
  | 'qualitative'
  | 'finalizing'
  | 'commitments'
  | 'previous-delivery'
  | 'requirements';

export type PreferencesStore = {
  openSaveSuccessModal: boolean;
  mode: StoreMode;
  geography: GeographyData | null;
  themes: ThemeData[];
  impact: ImpactData | null;
  scoring: ScoringData | null;
  qualitative: QualitativeData | null;
  finalizing: FinalizingData | null;
  completedSteps: PreferencesStep[];
  errors: Record<string, any>;
  hydrationVersion: number;
  setOpenSaveSuccessModal: (openSaveSuccessModal: boolean) => void;
  setMode: (mode: StoreMode) => void;
  setGeography: (data: GeographyData) => void;
  setThemes: (themes: ThemeData[]) => void;
  setImpact: (data: ImpactData) => void;
  setCommittedActivities: (committedActivities: ReportedActivityData[]) => void;
  setScoring: (data: ScoringData) => void;
  setQualitative: (data: QualitativeData) => void;
  setFinalizing: (data: FinalizingData) => void;
  setCompletedSteps: (data: PreferencesStep[]) => void;
  unmarkStepCompleted: (step: PreferencesStep) => void;
  setError: (errors: any) => void;
  resetPreferences: () => void;
  incrementHydration: () => void;
};

export const createPreferencesStore = () =>
  create<PreferencesStore>((set) => ({
    openSaveSuccessModal: false,
    mode: 'preferences',
    geography: null,
    themes: [],
    impact: { activities: [], committedActivities: [] },
    scoring: null,
    qualitative: null,
    finalizing: null,
    completedSteps: [],
    errors: {},
    hydrationVersion: 0,
    setOpenSaveSuccessModal: (openSaveSuccessModal) => set({ openSaveSuccessModal }),
    setMode: (mode) => set({ mode }),
    setGeography: (geography) => set({ geography }),
    setThemes: (themes) => set({ themes }),
    setImpact: (impact) => set({ impact }),
    setCommittedActivities: (committedActivities) =>
      set((state) => ({
        impact: { activities: state.impact?.activities || [], committedActivities },
      })),
    setScoring: (scoring) => set({ scoring }),
    setQualitative: (qualitative) => set({ qualitative }),
    setFinalizing: (finalizing) => set({ finalizing }),
    setCompletedSteps: (completedSteps: PreferencesStep[]) => set({ completedSteps }),
    unmarkStepCompleted: (step) =>
      set((state) => ({
        completedSteps: state.completedSteps.filter((s) => s !== step),
      })),
    setError: (errors) => set({ errors }),
    resetPreferences: () =>
      set({
        geography: null,
        themes: [],
        impact: null,
        scoring: null,
        qualitative: null,
        finalizing: null,
        completedSteps: [],
      }),
    incrementHydration: () => set((state) => ({ hydrationVersion: state.hydrationVersion + 1 })),
  }));

export const usePreferencesStore = createPreferencesStore();
